import { useState, useCallback } from "react";
import { useDebouncedCallback } from "./use-debounce";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

interface MutationLike<TVariables> {
  mutate: (
    variables: TVariables,
    options?: { onSuccess?: () => void; onError?: () => void },
  ) => void;
}

interface UseAutoSaveOptions<TVariables> {
  mutation: MutationLike<TVariables>;
  delay?: number;
}

/**
 * Encapsulates the auto-save pattern used by inline editors.
 * Composes useDebouncedCallback for text fields; exposes a direct `save` for selects.
 */
export function useAutoSave<TVariables>({
  mutation,
  delay = 1000,
}: UseAutoSaveOptions<TVariables>) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

  const save = useCallback(
    (data: TVariables) => {
      setSaveStatus("saving");
      mutation.mutate(data, {
        onSuccess: () => setSaveStatus("saved"),
        onError: () => setSaveStatus("error"),
      });
    },
    [mutation],
  );

  const { debouncedFn: debouncedSave } = useDebouncedCallback(save, delay);

  return { saveStatus, debouncedSave, save };
}
